import {
  siteNames,
  siteConfig,
  SiteNamesMatchingClassification,
} from '@seek/melways-sites';
import { oauth, Realm } from '@seek/oauth-url-builders';

import { useCallback } from 'react';
import { useAppConfig } from '../useAppConfig';
import { Environment } from 'src/types';
import { useAuthN, useTscAuth } from '../useTscAuth';
import { useGetTalentSearchUserContextQuery } from '../useTalentSearchContext/gql/GetTalentSearchUserContext.generated';

export type EmployerSiteName = SiteNamesMatchingClassification<{
  product: 'employer';
}>;

const getCountryCode = (countryInput?: string | null): 'nz' | 'au' =>
  countryInput === 'NZ' ? 'nz' : 'au';
function useWindow() {
  const authN = useAuthN();
  const { environment } = useAppConfig();
  const { identity } = useTscAuth();
  const advertiserId = identity?.advertiserId;
  const { data } = useGetTalentSearchUserContextQuery({
    skip: !advertiserId,
  });
  const managingCountry =
    data?.talentSearchUserContext?.hirerAccount.managingCountry;
  return {
    openTalentSearchUrl: useCallback(
      async (location: string) => {
        const user = await authN.getUser();

        if (!user) {
          throw new Error('User is undefined');
        }

        const realm: Realm = user.sub.startsWith('auth0|')
          ? 'Username-Password-Authentication'
          : 'seek-okta-global';

        const scope = [
          ...(user['http://seek/claims/advertiser_id']
            ? [`advertiser:${user['http://seek/claims/advertiser_id']}`]
            : []),
          ...(user['http://seek/claims/act_as/email']
            ? [`act:${user['http://seek/claims/act_as/email']}`]
            : []),
        ];

        const returnUri = `/talentsearch/${location}`;
        const country = getCountryCode(managingCountry);
        const siteName = siteNames.employer.seek[country];
        const origin = `https://${
          environment === Environment.Production
            ? siteConfig[siteName].host
            : siteConfig[siteName].stagingHost
        }`;

        const url = oauth.buildLoginWithScopeUrl({
          origin,
          realm,
          returnUri,
          scope,
        });

        window.open(url);
        window.opener = null;
        return false;
      },
      [authN, environment, managingCountry],
    ),
  };
}

export default useWindow;
